/** @jsxImportSource @emotion/react */
import { Header, TruncatedString } from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Icon,
  Input,
  Loader,
  Modal,
  Table,
  Typography,
  useTheme,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { capitalize, kebabCase } from 'lodash';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import {
  AlgorandProcessingRequestState,
  retryAlgorandProcessingRequest,
  useDetails,
} from 'api/useAlgorandProcessingApi';

export const AlgorandProcessingRequestDetails = () => {
  const { palette, spacing } = useTheme();
  const navigate = useNavigate();
  const { referenceId } = useParams();
  if (!referenceId) {
    throw new Error('referenceId is required');
  }

  const { data, isLoading, refetch } = useDetails(referenceId);

  const [oldFlowModalOpen, setOldFlowModalOpen] = useState(false);
  const [merchantId, setMerchantId] = useState('');

  const [{ loading: retryNewLoading }, retryNewFlow] = useAsyncFn(async () => {
    await retryAlgorandProcessingRequest(referenceId, false);
    await refetch();
  }, [referenceId, refetch]);

  const [{ loading: retryOldLoading }, retryOldFlow] = useAsyncFn(async () => {
    await retryAlgorandProcessingRequest(referenceId, true, merchantId);
    setOldFlowModalOpen(false);
    await refetch();
  }, [referenceId, merchantId, refetch]);
  const detailsFields: { key: string; value: string | null | undefined }[] =
    data
      ? [
          { key: 'referenceId', value: data.referenceId },
          { key: 'orderId', value: data.orderId },
          { key: 'emailAddress', value: data.emailAddress },
          { key: 'userId', value: data.userId },
          { key: 'walletAddress', value: data.walletAddress },
          { key: 'state', value: data.state },
          { key: 'euroAmount', value: `${data.euroAmount} EUR` },
          { key: 'createdAt', value: data.createdAt.toLocaleString() },
          {
            key: 'purchasedAt',
            value: data.purchasedAt ? data.purchasedAt.toLocaleString() : null,
          },
          { key: 'callbackUrl', value: data.callbackUrl },
        ]
      : [];

  return (
    <>
      <Header>
        <div
          css={{
            display: 'flex',
            gap: spacing.xxxs,
            alignItems: 'center',
          }}
        >
          <div
            css={{ display: 'flex', gap: spacing.xxxs, alignItems: 'center' }}
          >
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/algorand-processing')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Algorand processing
              </Typography>
            </div>
            <div
              css={{
                transform: 'rotate(180deg)',
                svg: {
                  fill: 'none',
                  stroke: palette.grey.x300,
                },
              }}
            >
              <Icon name="chevron" />
            </div>
            <Typography variant="subtitle">{referenceId}</Typography>
          </div>
          <div
            css={{
              marginLeft: 'auto',
              display: 'flex',
              gap: spacing.xxxs,
              alignItems: 'center',
            }}
          >
            {data &&
              data.state !== AlgorandProcessingRequestState.COMPLETED && (
                <>
                  <UtilityButton
                    variant="secondary"
                    text="Retry (new flow)"
                    onClick={retryNewFlow}
                    disabled={retryNewLoading}
                  />
                  <UtilityButton
                    variant="secondary"
                    text="Retry (old flow)"
                    onClick={() => setOldFlowModalOpen(true)}
                  />
                </>
              )}
          </div>
        </div>
      </Header>
      {isLoading && <Loader />}
      {data && (
        <div>
          <div
            css={{
              display: 'grid',
              gap: spacing.xxs,
              gridTemplateColumns: 'repeat(2, 1fr)',
            }}
          >
            {detailsFields.map(({ key, value }) => (
              <div
                key={key}
                css={{
                  '& .MuiInputBase-input.Mui-disabled': {
                    WebkitTextFillColor: palette.black,
                  },
                }}
              >
                <Input
                  label={capitalize(kebabCase(key).replace(/-/g, ' '))}
                  value={value ?? ''}
                  disabled
                  endAdornment={
                    key === 'userId' &&
                    data.userId && (
                      <UtilityButton
                        variant="icon"
                        icon="arrow"
                        css={{ svg: { transform: 'rotate(90deg)' } }}
                        onClick={() =>
                          navigate(`/users/processing/${data.userId}`)
                        }
                      />
                    )
                  }
                />
              </div>
            ))}
          </div>
          {data.transactions.length > 0 && (
            <Card css={{ marginTop: spacing.xxs }}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                Transactions
              </Typography>
              <Table
                headers={[
                  { label: 'Created At', cellKey: 'createdAt', sort: false },
                  { label: 'Status', cellKey: 'status', sort: false },
                  {
                    label: 'Blockchain Transaction ID',
                    cellKey: 'blockchainTransactionId',
                    sort: false,
                  },
                  { label: 'Amount (ALGO)', cellKey: 'amount', sort: false },
                  {
                    label: 'Network fee (ALGO)',
                    cellKey: 'networkFee',
                    sort: false,
                  },
                  {
                    label: 'Sender Address',
                    cellKey: 'senderAddress',
                    sort: false,
                  },
                  {
                    label: 'Receiver Address',
                    cellKey: 'receiverAddress',
                    sort: false,
                  },
                ]}
                rows={data.transactions.map((transaction) => ({
                  value: {
                    createdAt: transaction.createdAt.toLocaleString(),
                    status: transaction.status,
                    blockchainTransactionId: (
                      <TruncatedString
                        text={transaction.blockchainTransactionId}
                      />
                    ),
                    amount: transaction.amount,
                    networkFee: transaction.networkFee,
                    senderAddress: (
                      <TruncatedString text={transaction.senderAddress} />
                    ),
                    receiverAddress: (
                      <TruncatedString text={transaction.receiverAddress} />
                    ),
                  },
                }))}
              />
            </Card>
          )}
          {data.history.length > 0 && (
            <Card css={{ marginTop: spacing.xxs }}>
              <Typography variant="subtitle" color={palette.grey.x300}>
                History
              </Typography>
              <Table
                headers={[
                  { label: 'Created At', cellKey: 'createdAt', sort: false },
                  { label: 'State', cellKey: 'state', sort: false },
                ]}
                rows={data.history.map((history) => ({
                  value: {
                    createdAt: history.createdAt.toLocaleString(),
                    state: history.state,
                  },
                }))}
              />
            </Card>
          )}
        </div>
      )}
      {oldFlowModalOpen && (
        <Modal
          title="Retry (old flow)"
          primaryButtonProps={{
            text: retryOldLoading ? 'Retrying...' : 'Retry',
            onClick: retryOldFlow,
            disabled: !merchantId || retryOldLoading,
            loading: retryOldLoading,
          }}
          secondaryButtonProps={{
            text: 'Cancel',
            onClick: () => setOldFlowModalOpen(false),
          }}
          closeModal={() => setOldFlowModalOpen(false)}
        >
          <Input
            label="Merchant ID"
            value={merchantId}
            onChange={(e) => setMerchantId(e.target.value)}
          />
        </Modal>
      )}
    </>
  );
};
